import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AssignmentService from '../../services/AssignmentService';
import UserService from '../../services/UserService';
import { useTrackUsage } from '../../hooks/useTrackUsage';

const EditAssignmentDialog = ({ open, onClose, assignment, onUpdate }) => {
  useTrackUsage('EditAssignmentDialog', 'dialog', 'admin', open);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Level 1');
  const [dueDate, setDueDate] = useState(null);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('ASSIGNED');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const categories = ['Level 1', 'Level 2', 'Level 3'];
  const statuses = ['ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'OVERDUE'];

  useEffect(() => {
    if (open && assignment) {
      // Clear previous messages when the dialog opens
      setError(null);
      setSuccess(null);

      // Initialize form with assignment data
      setSelectedUser(assignment.assignedTo || '');
      setSelectedCategory(assignment.category || 'Level 1');
      setNotes(assignment.notes || '');
      setStatus(assignment.status || 'ASSIGNED');
      
      // Format due date for the datetime-local input
      if (assignment.dueDate) {
        setDueDate(new Date(assignment.dueDate));
      } else {
        setDueDate(null);
      }
      
      fetchUsers();
    }
  }, [open, assignment]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = JSON.parse(localStorage.getItem('authToken'));
      
      const response = await UserService.getAllUsers(authUser.email, authToken);
      if (response) {
        setUsers(response);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedUser || !dueDate || !selectedCategory) {
      setError("Please fill all required fields");
      return;
    }

    try {
      setLoading(true);
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = JSON.parse(localStorage.getItem('authToken'));
      
      // Create updated assignment object
      const updatedAssignment = {
        ...assignment,
        assignedTo: selectedUser,
        category: selectedCategory,
        dueDate: dueDate.toISOString(),
        notes: notes,
        status: status
      };
      
      // Call API to update assignment
      await AssignmentService.updateAssignment(
        assignment.videoId,
        updatedAssignment,
        authUser.email,
        authToken
      );
      
      setSuccess("Assignment updated successfully");
      
      // Notify parent component about the update
      if (onUpdate) {
        onUpdate();
      }
      
      // Close dialog after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error("Error updating assignment:", error);
      setError("Failed to update assignment: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset messages on close
    setError(null);
    setSuccess(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <EditIcon sx={{ mr: 1 }} />
        Edit Assignment
      </DialogTitle>
      
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        <Typography variant="subtitle1" gutterBottom>
          Editing assignment for video "{assignment?.videoName}" in {assignment?.languageName}
        </Typography>
        
        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth margin="normal">
            <InputLabel id="user-label">Assigned To</InputLabel>
            <Select
              labelId="user-label"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              label="Assigned To"
              disabled={loading}
            >
              {users.map((user) => (
                <MenuItem key={user.email} value={user.email}>
                  {user.name || user.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="category-label">Category</InputLabel>
            <Select
              labelId="category-label"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              label="Category"
              disabled={loading}
            >
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              label="Status"
              disabled={loading}
            >
              {statuses.map((statusOption) => (
                <MenuItem key={statusOption} value={statusOption}>
                  {statusOption.replace('_', ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            label="Due Date"
            type="datetime-local"
            value={dueDate ? new Date(dueDate).toISOString().slice(0, 16) : ''}
            onChange={(e) => setDueDate(new Date(e.target.value))}
            fullWidth
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: new Date().toISOString().slice(0, 16)
            }}
            disabled={loading}
          />
          
          <TextField
            label="Notes (optional)"
            multiline
            rows={3}
            fullWidth
            margin="normal"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={loading}
          />
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={!selectedUser || !dueDate || !selectedCategory || loading}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAssignmentDialog;